import { createSlice } from '@reduxjs/toolkit';
import {
  catalogFiltersReset,
  catalogInStockOnlyToggled,
  catalogRecentQueriesCleared,
  catalogSearchChanged,
  catalogSearchCommitted,
  catalogSortChanged,
} from '@/shared/lib/redux/actions';

const RECENT_QUERIES_KEY = 'recentSearchQueries';
const MAX_RECENT_QUERIES = 10;

export interface CatalogFiltersState {
  search: string;
  sort: string | null;
  inStockOnly: boolean;
  recentQueries: string[];
}

function loadRecentQueries(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_QUERIES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((q): q is string => typeof q === 'string') : [];
  } catch {
    return [];
  }
}

const initialState: CatalogFiltersState = {
  search: '',
  sort: null,
  inStockOnly: false,
  recentQueries: loadRecentQueries(),
};

const catalogFiltersSlice = createSlice({
  name: 'catalogFilters',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(catalogSearchChanged, (state, action) => {
        state.search = action.payload;
      })
      .addCase(catalogSortChanged, (state, action) => {
        state.sort = action.payload;
      })
      .addCase(catalogInStockOnlyToggled, (state, action) => {
        state.inStockOnly = action.payload;
      })
      .addCase(catalogSearchCommitted, (state, action) => {
        const query = action.payload.trim();
        if (!query) return;
        state.recentQueries = [query, ...state.recentQueries.filter((q) => q !== query)].slice(
          0,
          MAX_RECENT_QUERIES,
        );
        localStorage.setItem(RECENT_QUERIES_KEY, JSON.stringify(state.recentQueries));
      })
      .addCase(catalogRecentQueriesCleared, (state) => {
        state.recentQueries = [];
        localStorage.removeItem(RECENT_QUERIES_KEY);
      })
      .addCase(catalogFiltersReset, () => initialState);
  },
});

export const catalogFiltersReducer = catalogFiltersSlice.reducer;
